import React, { useState, useRef, useEffect } from 'react';
import { UserProfile } from '../../utils/interfaces';
import { SIDEBAR_CATEGORIES } from '../../utils/constants';
import { createDefaultUserProfile } from '../../utils/useUserProfile';
import '../../App.css';
import './ProfileModal.css';
import GeneralSection from './modal_sections/GeneralSection';
import BallSection from './modal_sections/BallSection';
import ClubsSection from './modal_sections/ClubsSection';

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeCategory: string | null;
  editableProfile: UserProfile | null;
  handleProfileChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  handleProfileSave: () => void;
  loading: boolean;
  onContinueNextCategory: (category: string) => void; // Optional prop for category change
  showAddClubTutorial?: boolean;
  onDismissAddClubTutorial?: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({
  isOpen,
  onClose,
  activeCategory,
  editableProfile,
  handleProfileChange,
  handleProfileSave,
  loading,
  onContinueNextCategory,
  showAddClubTutorial,
  onDismissAddClubTutorial
}) => {
  const [showAddClubDropdown, setShowAddClubDropdown] = useState(false);
  const [infoModalType, setInfoModalType] = useState<'wrist-to-floor' | 'swing-type' | null>(null);
  const addClubDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (addClubDropdownRef.current && !addClubDropdownRef.current.contains(event.target as Node)) {
        setShowAddClubDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const resolvedProfile = editableProfile ?? (createDefaultUserProfile() as UserProfile);

  if (!isOpen || !activeCategory) return null;

  // Get clubs that belong to the current category
  const getClubsInCategory = () => {
    if (activeCategory === 'General' || activeCategory === 'Ball') return [];
    
    const clubsForCategory = SIDEBAR_CATEGORIES[activeCategory] || [];
    return clubsForCategory.filter(clubName => 
      resolvedProfile[clubName as keyof UserProfile] !== undefined
    );
  };

  // Get available clubs that can be added to this category
  const getAvailableClubsForCategory = () => {
    if (activeCategory === 'General' || activeCategory === 'Ball') return [];
    
    const clubsForCategory = SIDEBAR_CATEGORIES[activeCategory] || [];
    return clubsForCategory.filter(clubName => 
      resolvedProfile[clubName as keyof UserProfile] === undefined
    );
  };

  // Add a new club to the profile
  const handleAddClub = (clubName: string) => {
    const syntheticEvent = {
      target: {
        name: `${clubName}.model`,
        value: '' // Ensure model is an empty string, not null
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handleProfileChange(syntheticEvent);
    setShowAddClubDropdown(false);
  };

  // Remove a club from the profile
  const handleRemoveClub = (clubName: string) => {
    // Create a synthetic event to remove the club by setting it to undefined
    const syntheticEvent = {
      target: {
        name: clubName,
        value: 'DELETE_CLUB' // Special value to indicate club deletion
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handleProfileChange(syntheticEvent);
  };

const nextCategory = activeCategory === 'General' ? 'Driver' 
  : activeCategory === 'Driver' ? 'Fairway Woods'
  : activeCategory === 'Fairway Woods' ? 'Utilities'
  : activeCategory === 'Utilities' ? 'Irons'
  : activeCategory === 'Irons' ? 'Wedges'
  : activeCategory === 'Wedges' ? 'Putter'
  : activeCategory === 'Putter' ? 'Ball'
  : 'General'; // Ball or any other category goes back to General

const nextCategoryLabel = nextCategory === 'General' ? 'General Profile →'
  : nextCategory === 'Driver' ? 'Driver Profile →'
  : nextCategory === 'Fairway Woods' ? 'Fairway Woods →'
  : nextCategory === 'Utilities' ? 'Utilities Profile →'
  : nextCategory === 'Irons' ? 'Irons Profile →'
  : nextCategory === 'Wedges' ? 'Wedges Profile →'
  : nextCategory === 'Putter' ? 'Putter Profile →'
  : nextCategory === 'Ball' ? 'Ball Profile →'
  : 'General Profile →'; // Fallback to General if none match

  const handleSaveAndClose = async () => {
    await handleProfileSave();
    onClose();
  };

  // Handle next-category click without closing the modal
  const handleNextCategory = async (category: string) => {
    await handleProfileSave();

    if (onContinueNextCategory) {
      onContinueNextCategory(category);
    } else {
      onClose();
    }
  };

  const currentClubs = getClubsInCategory();
  const availableClubs = getAvailableClubsForCategory();

  const isMobileContext = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div
      className={`profile-modal-overlay${isMobileContext ? ' profile-modal-overlay--mobile' : ''}`}
      style={{
        position: 'fixed',
        top: 'calc(0px)',
        left: 0,
        width: '100%',
        height: '100vh',
        zIndex: 2000, // higher than Messenger's 1200
        background: 'rgba(0,0,0,0.3)', // optional
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div className={`modal-content${isMobileContext ? ' modal-content--mobile' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{activeCategory} Profile</h2>
          {activeCategory !== 'General' && activeCategory !== 'Ball' 
          && activeCategory !== 'Driver' && activeCategory !== 'Putter' && availableClubs.length > 0 && (
            <div className="add-club-section" ref={addClubDropdownRef} style={{ position: 'relative' }}>
              <button onClick={() => setShowAddClubDropdown(!showAddClubDropdown)} className="add-club-btn">
                + Add Club
              </button>
              
              {/* Add Club Tutorial - positioned directly under the Add Club button */}
              {showAddClubTutorial && !(typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent)) && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 10001,
                  marginTop: '20px'
                }}>
                  <div style={{
                    position: 'relative',
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    padding: '1.2rem',
                    width: '260px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
                    border: '2px solid #1976d2'
                  }}>
                    {/* Arrow pointing up to Add Club button */}
                    <div style={{
                      position: 'absolute',
                      top: '-30px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: 0,
                      height: 0,
                      borderLeft: '25px solid transparent',
                      borderRight: '25px solid transparent',
                      borderBottom: '30px solid #1976d2'
                    }} />
                    
                    <div style={{
                      textAlign: 'left'
                    }}>
                      <div style={{
                        fontSize: '1.2rem',
                        marginBottom: '0.8rem',
                        color: '#1976d2',
                        fontWeight: '600'
                      }}>
                        ➕ Add your clubs!
                      </div>
                      
                      <p style={{
                        color: '#333',
                        marginBottom: '1rem',
                        fontSize: '0.9rem',
                        lineHeight: '1.3',
                        fontWeight: '500'
                      }}>
                        Click "Add Club" to tell us about your current clubs
                      </p>
                      
                      <p style={{
                        color: '#666',
                        marginBottom: '1rem',
                        fontSize: '0.85rem',
                        lineHeight: '1.3'
                      }}>
                        The more details you provide, the better our recommendations will be!
                      </p>
                      
                      <button
                        onClick={onDismissAddClubTutorial}
                        style={{
                          backgroundColor: '#1976d2',
                          color: '#fff',
                          border: 'none',
                          padding: '0.6rem 1.2rem',
                          borderRadius: '6px',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s ease',
                          width: '100%'
                        }}
                        onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#1565c0'}
                        onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#1976d2'}
                      >
                        Got it!
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {showAddClubDropdown && (
                <div className="add-club-dropdown">
                  {availableClubs.map(clubName => (
                    <button
                      key={clubName}
                      type="button"
                      className="add-club-option"
                      onClick={() => handleAddClub(clubName)}
                    >
                      {clubName}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <button onClick={handleProfileSave} className="close-button">&times;</button>
        </div>
        <div className="modal-body">
          {activeCategory === 'General' && (
            <GeneralSection 
              editableProfile={resolvedProfile}
              handleProfileChange={handleProfileChange}
              isOpen={isOpen}
              setInfoModalType={setInfoModalType}
            />
          )}

          {activeCategory === 'Ball' && (
            <BallSection 
              editableProfile={resolvedProfile}
              handleProfileChange={handleProfileChange}
            />
          )}
          
          {activeCategory !== 'General' && activeCategory !== 'Ball' && (
            <ClubsSection 
              activeCategory={activeCategory}
              currentClubs={currentClubs}
              editableProfile={resolvedProfile}
              handleProfileChange={handleProfileChange}
              handleRemoveClub={handleRemoveClub}
            />
          )}
        </div>
        <div className="modal-footer">
          <button className="nf-btn nf-btn-secondary" onClick={handleSaveAndClose} disabled={loading}>
            {loading ? 'Saving...' : 'Save & Close'}
          </button>
          <button className="nf-btn nf-btn-primary" onClick={() => handleNextCategory(nextCategory)} disabled={loading}>
            {nextCategoryLabel}
          </button>
        </div>
      </div>

      {infoModalType && (
        <div className="info-modal-overlay" onClick={() => setInfoModalType(null)}>
          <div className="info-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="info-modal-header">
              <h2>{infoModalType === 'wrist-to-floor' ? 'Measuring Your Wrist-to-Floor' : 'Understanding Swing Types'}</h2>
              <button onClick={() => setInfoModalType(null)} className="close-button">&times;</button>
            </div>
            <div className="info-modal-body">
              {infoModalType === 'wrist-to-floor' ? (
                <>
                  <div className="tooltip-instructions">
                    <div className="tooltip-step">
                      <strong>1. Posture:</strong> Stand tall on a hard, flat surface with your shoulders relaxed.
                    </div>
                    <div className="tooltip-step">
                      <strong>2. Arm Position:</strong> Let your arms hang naturally by your sides.
                    </div>
                    <div className="tooltip-step">
                      <strong>3. Shoes:</strong> Wear the shoes you typically golf in.
                    </div>
                    <div className="tooltip-step">
                      Have a friend measure from the crease of your wrist straight down to the floor.
                    </div>
                  </div>
                  <div className="tooltip-chart">
                    <div className="tooltip-chart-title">Reference Chart</div>
                    <div className="tooltip-chart-header">
                      <span>Wrist-to-Floor (in)</span>
                      <span>Length Adjustment</span>
                    </div>
                    <div className="tooltip-chart-row">
                      <span>41" - 43"</span>
                      <span>+2.00"</span>
                    </div>
                    <div className="tooltip-chart-row">
                      <span>39" - 41"</span>
                      <span>+1.50"</span>
                    </div>
                    <div className="tooltip-chart-row">
                      <span>37" - 39"</span>
                      <span>+1.00"</span>
                    </div>
                    <div className="tooltip-chart-row">
                      <span>35" - 37"</span>
                      <span>+0.50"</span>
                    </div>
                    <div className="tooltip-chart-row">
                      <span>33" - 35"</span>
                      <span>Standard</span>
                    </div>
                    <div className="tooltip-chart-row">
                      <span>31" - 33"</span>
                      <span>-0.50"</span>
                    </div>
                    <div className="tooltip-chart-row">
                      <span>29" - 31"</span>
                      <span>-1.00"</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="tooltip-instructions">
                  <div className="tooltip-step">
                    <strong>Digger:</strong> Takes large divots after impact. Typically has a steeper angle of attack and compresses the ball well. May benefit from clubs with stronger lofts and more offset.
                  </div>
                  <div className="tooltip-step">
                    <strong>Neutral:</strong> Takes small, consistent divots after impact. Has a balanced angle of attack that works well with standard club specifications and lie angles.
                  </div>
                  <div className="tooltip-step">
                    <strong>Sweeper:</strong> Takes very little to no divots. Typically has a shallow angle of attack and may pick the ball clean. Often benefits from clubs with weaker lofts and less offset.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileModal;
